'use client';

import { VisitorJourney } from './visitor-journey';
import Script from 'next/script';
import { Suspense, useCallback, useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Analytics } from '@vercel/analytics/next';
import { telemetryUrl } from '@/lib/telemetry-privacy';
import { createGooglePageViewTracker } from '@/lib/google-pageviews';

declare global {
  interface Window {
    gtag?: (command: 'config' | 'event' | 'js', targetId: string | Date, config?: Record<string, unknown>) => void;
    dataLayer?: ArrayLike<unknown>[];
  }
}

const GOOGLE_READY = 'shipzy:google-analytics-ready';

function GoogleNavigationViews({ onNavigation }: { onNavigation: () => void }) {
  const pathname = usePathname();
  const search = useSearchParams().toString();
  useEffect(() => {
    onNavigation();
    window.addEventListener(GOOGLE_READY, onNavigation);
    return () => window.removeEventListener(GOOGLE_READY, onNavigation);
  }, [pathname, search, onNavigation]);
  return null;
}

export function PrivacyAnalytics() {
  const initialized = useRef(false);
  const pageView = useRef(createGooglePageViewTracker());
  const trackNavigation = useCallback(() => {
    if (!initialized.current || !window.gtag) return;
    pageView.current(window.location.href, document.referrer, document.title,
      parameters => window.gtag?.('event', 'page_view', parameters));
  }, []);
  const initializeGoogle = useCallback(() => {
    if (initialized.current) return;
    initialized.current = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function(command, target, config) {
      const referrer = typeof config?.page_referrer === 'string' ? config.page_referrer : document.referrer;
      const safe: Record<string, unknown> = { ...config, page_location: telemetryUrl(window.location.href),
        page_referrer: referrer ? telemetryUrl(referrer) : '' };
      if (typeof safe.page_path === 'string') safe.page_path = telemetryUrl(safe.page_path);
      // eslint-disable-next-line prefer-rest-params -- Preserve Google's documented gtag Arguments command format.
      if (command !== 'js') { arguments[2] = safe; arguments.length = 3; }
      // eslint-disable-next-line prefer-rest-params -- Google consumes the Arguments command record.
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    // The stream's Enhanced Measurement history and Site Search must also be disabled.
    window.gtag('config', 'G-5K70WWREK2', { send_page_view: false });
    window.dispatchEvent(new Event(GOOGLE_READY));
  }, []);
  useEffect(initializeGoogle, [initializeGoogle]);
  return <>
    <VisitorJourney initializeAnalytics={initializeGoogle} />
    <Script src="https://www.googletagmanager.com/gtag/js?id=G-5K70WWREK2" strategy="afterInteractive" onReady={initializeGoogle} />
    <Analytics beforeSend={event => ({ ...event, url: telemetryUrl(event.url) })} />
    <Suspense fallback={null}>
      <GoogleNavigationViews onNavigation={trackNavigation} />
    </Suspense>
  </>;
}
